// UserPermission.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Filter, UserCog, X, ChevronDown, Loader, Shield, Plus, CheckCircle } from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import editIcon from '../assets/Edit (1).svg';
import deleteIcon from '../assets/deleteBtn.svg';
import { permissionsApi, designationsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
/**
 * Toggle Component
 */
function Toggle({ checked, onChange, ariaLabel }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.textColor?.toLowerCase() === '#ffffff';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors border-2 border-white shadow-sm ${checked ? '' : (isDark ? 'bg-white/10' : 'bg-gray-300')}`}
      style={{ backgroundColor: checked ? currentTheme.color : undefined }}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

/**
 * Checkbox Component
 */
function Checkbox({ checked, onChange, ariaLabel }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.textColor?.toLowerCase() === '#ffffff';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className="w-4 h-4 border-2 rounded flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={{
        backgroundColor: checked ? currentTheme.color : (isDark ? 'transparent' : 'white'),
        borderColor: checked ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.2)' : '#D1D5DB'),
      }}
    >
      {checked && (
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
}

/**
 * UserPermission — Designation table + Designation-based Permission Management
 */
function UserPermission() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.textColor?.toLowerCase() === '#ffffff';

  // ============================================
  // STATE
  // ============================================
const navigate = useNavigate();
  // Designations (table data)
  const [designations, setDesignations] = useState([]);
  const [loadingDesignations, setLoadingDesignations] = useState(true);

  // Master permission list
  const [apiPermissions, setApiPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Search & pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Permission categories (modal UI)
  const [permissionCategories, setPermissionCategories] = useState([]);

  // Assign Permissions Modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [modalDesignationId, setModalDesignationId] = useState(null);
  const [modalDesignationName, setModalDesignationName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingDesPerms, setLoadingDesPerms] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  // ============================================
  // BUILD CATEGORIES FROM MASTER PERMISSIONS
  // enabledIds = Set of permissionId integers already assigned
  // ============================================
  const buildCategories = (masterPerms, enabledIds = new Set()) => {
    if (!masterPerms || masterPerms.length === 0) return [];
    const map = {};

    masterPerms.forEach((p, i) => {
      if (!p || typeof p !== 'object') return;
      const cat = p.category || 'Uncategorized';
      const catId = cat.toLowerCase().replace(/\s+/g, '-');

      if (!map[cat]) map[cat] = { id: catId, name: cat, enabled: false, subPermissions: [] };

      const isEnabled = enabledIds.has(p.id);
      map[cat].subPermissions.push({
        id: p.code || `perm-${p.id || i}`,
        name: p.action || 'Unknown',
        displayName: p.displayName || p.action || 'Unknown',
        enabled: isEnabled,
        module: p.module || 'General',
        code: p.code,
        permissionId: p.id
      });
    });

    return Object.values(map).map(cat => ({
      ...cat,
      enabled: cat.subPermissions.some(s => s.enabled)
    }));
  };

  // ============================================
  // INITIAL LOAD
  // ============================================
  useEffect(() => {
    fetchDesignations();
    loadMasterPermissions();
  }, []);

  const fetchDesignations = async () => {
    try {
      setLoadingDesignations(true);
      const data = await designationsApi.getAllDesignations();
      setDesignations(data || []);
    } catch (err) {
      console.error('Error loading designations:', err);
      setDesignations([]);
    } finally {
      setLoadingDesignations(false);
    }
  };

  const loadMasterPermissions = async () => {
    try {
      setLoadingPermissions(true);
      const data = await permissionsApi.getAllPermissions();
      setApiPermissions(data || []);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setApiPermissions([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Build blank categories once master list is ready
  useEffect(() => {
    if (apiPermissions.length > 0) {
      const cats = buildCategories(apiPermissions);
      setPermissionCategories(cats);
      if (cats.length > 0) setSelectedCategory(cats[0].id);
    }
  }, [apiPermissions]);

  // ============================================
  // FILTER & PAGINATION
  // ============================================
  const filteredDesignations = useMemo(() => {
    if (!searchTerm.trim()) return designations;
    const lower = searchTerm.toLowerCase();
    return designations.filter(d => {
      const name = typeof d === 'string' ? d : d.name;
      const desc = typeof d === 'object' ? (d.description || '') : '';
      return name?.toLowerCase().includes(lower) || desc.toLowerCase().includes(lower);
    });
  }, [designations, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredDesignations.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageDesigs = filteredDesignations.slice((safePage - 1) * pageSize, safePage * pageSize);

  // ============================================
  // DESIGNATION TABLE HANDLERS
  // ============================================
  const handleSearch = (val) => { setSearchTerm(val); setCurrentPage(1); };

  const handleDeleteDesignation = async (desId, desName) => {
    if (!window.confirm(`Delete designation "${desName}"?`)) return;
    try {
      await designationsApi.deleteDesignation(desId);
      setDesignations(prev => prev.filter(d => (typeof d === 'object' ? d.id : d) !== desId));
    } catch {
      message.error('Failed to delete designation');
    }
  };

  // ============================================
  // OPEN MODAL — load designation's existing permissions
  // ============================================
  const openModal = async (designation) => {
    const desId = typeof designation === 'string' ? null : designation.id;
    const desName = typeof designation === 'string' ? designation : designation.name;

    setModalDesignationId(desId);
    setModalDesignationName(desName);
    setShowAssignModal(true);

    if (desId) {
      await loadDesignationPermissions(desId);
    } else {
      // No ID — blank slate
      const blank = buildCategories(apiPermissions);
      setPermissionCategories(blank);
      if (blank.length > 0) setSelectedCategory(blank[0].id);
    }
  };

  const loadDesignationPermissions = async (desId) => {
    try {
      setLoadingDesPerms(true);
      // GET /designations/{id}/permissions
      const existing = await permissionsApi.getPermissionsByDesignation(desId);
      console.log('Existing designation permissions:', existing);

      const enabledIds = new Set(
        (existing || []).map(p => p.id ?? p.permissionId).filter(Boolean)
      );

      const cats = buildCategories(apiPermissions, enabledIds);
      setPermissionCategories(cats);
      if (cats.length > 0) setSelectedCategory(cats[0].id);
    } catch (err) {
      console.error('Error loading designation permissions:', err);
      const blank = buildCategories(apiPermissions);
      setPermissionCategories(blank);
    } finally {
      setLoadingDesPerms(false);
    }
  };
 const handleAddermission = () => {
    navigate('/employees/createPermission');
  };
  const closeModal = () => {
    setShowAssignModal(false);
    setModalDesignationId(null);
    setModalDesignationName('');
    setSelectedCategory('');
    if (apiPermissions.length > 0) {
      const blank = buildCategories(apiPermissions);
      setPermissionCategories(blank);
    }
  };

  // ============================================
  // PERMISSION TOGGLE HANDLERS
  // ============================================
  const handleCategoryToggle = (catId, enabled) => {
    setPermissionCategories(prev =>
      prev.map(cat => cat.id === catId ? { ...cat, enabled } : cat)
    );
  };

  const handleSubToggle = (catId, subId, enabled) => {
    setPermissionCategories(prev =>
      prev.map(cat => {
        if (cat.id !== catId) return cat;
        const subs = cat.subPermissions.map(s => s.id === subId ? { ...s, enabled } : s);
        return { ...cat, subPermissions: subs };
      })
    );
  };

  const handleSelectAll = (catId, select) => {
    setPermissionCategories(prev =>
      prev.map(cat =>
        cat.id !== catId ? cat : {
          ...cat,
          enabled: select,
          subPermissions: cat.subPermissions.map(s => ({ ...s, enabled: select }))
        }
      )
    );
  };

  // ============================================
  // SAVE — PUT /designations/{id} with permissionIds
  // ============================================
  const handleSavePermissions = async () => {
    if (!modalDesignationId) { message.error('Designation ID not found'); return; }

    const selectedPermissionIds = permissionCategories
      .flatMap(cat => cat.subPermissions)
      .filter(sub => sub.enabled)
      .map(sub => {
        const perm = apiPermissions.find(p => p.code === sub.code);
        return perm?.id;
      })
      .filter(Boolean);

    if (selectedPermissionIds.length === 0) {
      message.warning('Please select at least one permission');
      return;
    }

    try {
      setSavingPerms(true);

      // Resolve designation name & description
      const desObj = designations.find(d => (typeof d === 'object' ? d.id : null) === modalDesignationId);
      const desName = typeof desObj === 'object' ? desObj.name : modalDesignationName;
      const desDesc = typeof desObj === 'object' ? (desObj.description || '') : '';

      // PUT /designations/{id}
      await designationsApi.updateDesignation(modalDesignationId, {
        name: desName,
        description: desDesc,
        permissionIds: selectedPermissionIds
      });

      message.success(`Permissions saved for "${modalDesignationName}".`);
      closeModal();
    } catch (err) {
      console.error('Error saving permissions:', err);
      message.error('Failed to save permissions');
    } finally {
      setSavingPerms(false);
    }
  };

  // ============================================
  // DERIVED
  // ============================================
  const selectedCategoryData = permissionCategories.find(c => c.id === selectedCategory);
  const selectedPermissionsCount = permissionCategories.flatMap(c => c.subPermissions).filter(s => s.enabled).length;

  // ============================================
  // LOADING
  // ============================================
  if (loadingDesignations || loadingPermissions) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: currentTheme.color }} />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h1 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>User Permission</h1>
          <ExpandableSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            placeholder="Search designations..."
          />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            className={`w-10 h-10 rounded-full border transition-colors flex items-center justify-center ${isDark ? 'border-white/10 bg-white/5 hover:bg-white/10' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
            title="Filter"
          >
            <Filter className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
              <button
                onClick={() => { handleAddermission() }}
                className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-bold shadow-lg transition-all active:scale-[0.98] border-2 border-white"
                style={{ backgroundColor: currentTheme.color }}
              >
                <Plus className="w-5 h-5" />
                Add New User Permission
              </button>
        </div>
      </div>

      {/* ── Designations Table ── */}
      <div className={`rounded-lg border overflow-hidden shadow-sm transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-white" style={{ background: currentTheme.color }}>
                <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider">#</th>
                <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider">DESIGNATION</th>
                <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider">DESCRIPTION</th>
                <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider">PERMISSIONS</th>
                <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider">ACTION</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageDesigs.map((des, idx) => {
                const desId = typeof des === 'object' ? des.id : null;
                const desName = typeof des === 'object' ? des.name : des;
                const desDesc = typeof des === 'object' ? (des.description || '—') : '—';
                const permCount = typeof des === 'object' ? (des.permissionIds?.length ?? des.permissions?.length ?? null) : null;

                return (
                  <tr key={desId ?? desName} className={`transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>

                    {/* # */}
                    <td className={`px-4 py-3.5 text-sm whitespace-nowrap ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {(safePage - 1) * pageSize + idx + 1}
                    </td>

                    {/* Name */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${currentTheme.color}18` }}>
                          <Shield className="w-4 h-4" style={{ color: currentTheme.color }} />
                        </div>
                        <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{desName}</span>
                      </div>
                    </td>

                    {/* Description */}
                    <td className={`px-4 py-3.5 text-sm max-w-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {desDesc}
                    </td>

                    {/* Permission count badge */}
                    <td className="px-4 py-3.5">
                      {permCount !== null ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold"
                          style={{ backgroundColor: `${currentTheme.color}15`, color: currentTheme.color }}>
                          {permCount} permissions
                        </span>
                      ) : (
                        <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        {/* Assign Permissions */}
                        <button
                          type="button"
                          onClick={() => openModal(des)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors"
                          style={{ borderColor: currentTheme.color, color: currentTheme.color, backgroundColor: `${currentTheme.color}08` }}
                          title="Assign Permissions"
                        >
                          <UserCog className="w-3.5 h-3.5" />
                          Permissions
                        </button>

                        {/* Delete */}
                        {desId && (
                          <button
                            type="button"
                            onClick={() => handleDeleteDesignation(desId, desName)}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors ${isDark ? 'border-red-900/50 bg-red-900/10 text-red-400 hover:bg-red-900/20' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'}`}
                            title="Delete"
                          >
                            <img src={deleteIcon} alt="Delete" className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {pageDesigs.length === 0 && (
                <tr>
                  <td colSpan={5} className={`px-4 py-12 text-center ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>
                    No designations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end space-x-1 mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={safePage === 1}
            className={`p-2 rounded-lg border transition-colors ${safePage === 1 ? (isDark ? 'border-white/5 text-gray-700 bg-[#1e293b]' : 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50') : (isDark ? 'border-white/10 text-gray-300 hover:bg-white/5 bg-[#1e293b]' : 'border-gray-300 text-gray-700 hover:bg-gray-50 bg-white')}`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pn;
            if (totalPages <= 5) pn = i + 1;
            else if (safePage <= 3) pn = i + 1;
            else if (safePage >= totalPages - 2) pn = totalPages - 4 + i;
            else pn = safePage - 2 + i;
            return (
              <button key={pn} onClick={() => setCurrentPage(pn)}
                className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${safePage === pn ? 'text-white shadow-sm' : (isDark ? 'border border-white/10 text-gray-400 hover:bg-white/5 bg-[#1e293b]' : 'border border-gray-300 text-gray-700 hover:bg-gray-50 bg-white')}`}
                style={{ backgroundColor: safePage === pn ? currentTheme.color : undefined }}>
                {pn}
              </button>
            );
          })}

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages}
            className={`p-2 rounded-lg border transition-colors ${safePage === totalPages ? (isDark ? 'border-white/5 text-gray-700 bg-[#1e293b]' : 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50') : (isDark ? 'border-white/10 text-gray-300 hover:bg-white/5 bg-[#1e293b]' : 'border-gray-300 text-gray-700 hover:bg-gray-50 bg-white')}`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          ASSIGN PERMISSIONS MODAL
      ═══════════════════════════════════════════ */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div className={`rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col ${isDark ? 'bg-[#0f172a] border border-white/5' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className={`px-6 py-4 flex items-center justify-between border-b ${isDark ? 'border-white/5' : ''}`}
              style={{ background: !isDark ? `linear-gradient(to bottom, ${currentTheme.color}15, ${currentTheme.color}05)` : undefined }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${currentTheme.color}20` }}>
                  <Shield className="w-5 h-5" style={{ color: currentTheme.color }} />
                </div>
                <div>
                  <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Assign Permissions</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Designation: <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>{modalDesignationName}</strong>
                  </p>
                </div>
                {selectedPermissionsCount > 0 && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white ml-2"
                    style={{ backgroundColor: currentTheme.color }}>
                    {selectedPermissionsCount} selected
                  </span>
                )}
              </div>
              <button type="button" onClick={closeModal}
                className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-white/50'}`}>
                <X className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>

            {/* Body */}
            <div className={`flex flex-1 overflow-hidden ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
                {/* Left: Category list */}
                <div className={`w-1/3 border-r overflow-y-auto ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-100'}`}>
                  <div className={`p-4 border-b text-xs font-bold uppercase tracking-wider ${isDark ? 'border-white/5 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
                    Permission Categories
                  </div>
                  <div className="p-2 space-y-1">
                    {loadingDesPerms ? (
                      <div className="text-center py-12">
                        <Loader className="w-6 h-6 animate-spin mx-auto mb-2" style={{ color: currentTheme.color }} />
                        <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Loading existing permissions...</p>
                      </div>
                    ) : permissionCategories.length > 0 ? (
                      permissionCategories.map(cat => {
                        const active = selectedCategory === cat.id;
                        return (
                          <div
                            key={cat.id}
                            onClick={() => setSelectedCategory(cat.id)}
                            className={`px-4 py-3 rounded-xl cursor-pointer transition-all flex items-center justify-between group ${
                              active 
                                ? (isDark ? 'bg-white/5 text-white shadow-sm' : 'bg-gray-50 text-gray-900 shadow-sm')
                                : (isDark ? 'text-gray-500 hover:bg-white/5 hover:text-gray-300' : 'text-gray-600 hover:bg-gray-50')
                            }`}
                            style={active ? { borderLeft: `4px solid ${currentTheme.color}` } : {}}
                          >
                            <span className="font-semibold text-sm truncate">{cat.name}</span>
                            <div className="flex items-center gap-2">
                              {cat.enabled && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                              <ChevronRight className={`w-4 h-4 transition-transform ${active ? 'translate-x-1' : ''} ${isDark ? 'text-white/10 group-hover:text-white/30' : 'text-gray-200'}`} />
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No permissions</p>
                    )}
                  </div>
                </div>

                {/* Right: Permission detail */}
                <div className={`flex-1 overflow-y-auto p-6 ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}>

                {loadingDesPerms ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Loader className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: currentTheme.color }} />
                      <p className="text-sm text-gray-500">Loading permissions for <strong>{modalDesignationName}</strong>...</p>
                    </div>
                  </div>
                ) : selectedCategoryData ? (
                  <div className="space-y-5">
                    {/* Category title + toggle */}
                    <div className={`flex items-center gap-3 pb-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <h3 className={`text-lg font-bold ${isDark ? 'text-white' : ''}`} style={!isDark ? { color: currentTheme.color } : {}}>
                        {selectedCategoryData.name}
                      </h3>
                      <Toggle
                        checked={selectedCategoryData.enabled}
                        onChange={val => handleCategoryToggle(selectedCategoryData.id, val)}
                        ariaLabel="Toggle all"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Full Access</span>
                    </div>

                    {/* Grid */}
                    {selectedCategoryData.subPermissions.length > 0 && (
                      <div className={`border rounded-xl p-5 ${isDark ? 'border-white/5 bg-white/5' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-700'}`}>
                            Actions
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                              {selectedCategoryData.subPermissions.length}
                            </span>
                          </h4>
                          <div className="flex gap-3 text-xs">
                            <button type="button"
                              className="font-medium underline underline-offset-2 transition-colors"
                              style={{ color: currentTheme.color }}
                              onClick={() => handleSelectAll(selectedCategoryData.id, true)}>
                              Select all
                            </button>
                            <span className={isDark ? 'text-white/10' : 'text-gray-300'}>|</span>
                            <button type="button"
                              className={`font-medium underline underline-offset-2 transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                              onClick={() => handleSelectAll(selectedCategoryData.id, false)}>
                              Deselect all
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-3">
                          {selectedCategoryData.subPermissions.map(sub => (
                            <div
                              key={sub.id}
                              className="flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-all"
                              style={{
                                borderColor: sub.enabled ? `${currentTheme.color}50` : (isDark ? 'rgba(255,255,255,0.05)' : '#E5E7EB'),
                                backgroundColor: sub.enabled ? `${currentTheme.color}08` : (isDark ? 'rgba(255,255,255,0.02)' : 'transparent')
                              }}
                              onClick={() => handleSubToggle(selectedCategoryData.id, sub.id, !sub.enabled)}
                            >
                              <div className="mt-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                <Checkbox
                                  checked={sub.enabled}
                                  onChange={val => handleSubToggle(selectedCategoryData.id, sub.id, val)}
                                  ariaLabel={`Toggle ${sub.name}`}
                                />
                              </div>
                              <div className="flex flex-col flex-1 min-w-0">
                                <span className={`text-sm font-bold truncate leading-tight ${isDark ? 'text-white' : 'text-gray-700'}`}>
                                  {sub.displayName}
                                </span>
                                {sub.module && (
                                  <span className={`text-xs truncate mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sub.module}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <Shield className="w-8 h-8 text-gray-300" />
                    </div>
                    <p className="text-gray-500 font-medium">Select a category on the left</p>
                    <p className="text-gray-400 text-sm mt-1">to view and configure permissions</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className={`px-6 py-4 border-t flex justify-between items-center ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {selectedPermissionsCount > 0
                  ? <><span className="font-bold" style={{ color: currentTheme.color }}>{selectedPermissionsCount}</span> permission(s) selected for <strong className={isDark ? 'text-white' : 'text-gray-700'}>{modalDesignationName}</strong></>
                  : <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>No permissions selected</span>
                }
              </span>

              <div className="flex gap-3">
                <button type="button" onClick={closeModal}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePermissions}
                  disabled={savingPerms || !modalDesignationId}
                  className="px-6 py-2.5 rounded-lg text-white font-medium flex items-center gap-2 transition-all shadow-md disabled:cursor-not-allowed"
                  style={{ backgroundColor: (savingPerms || !modalDesignationId) ? '#9CA3AF' : currentTheme.color }}
                >
                  {savingPerms
                    ? <><Loader className="w-4 h-4 animate-spin" /><span>Saving...</span></>
                    : <span>Save Permissions</span>
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserPermission;